
import { api } from './api';

export class SyncManager {
    private static instance: SyncManager;
    private isSyncing = false;
    private intervalId: NodeJS.Timeout | null = null;
    private statusListeners: Set<(status: 'Online' | 'Offline' | 'Syncing') => void> = new Set();
    private networkStatus: 'Online' | 'Offline' = navigator.onLine ? 'Online' : 'Offline';

    private constructor() {
        window.addEventListener('online', () => {
            this.networkStatus = 'Online';
            this.notifyListeners();
            this.sync();
        });
        window.addEventListener('offline', () => {
            this.networkStatus = 'Offline';
            this.notifyListeners();
        });
    }

    static getInstance(): SyncManager {
        if (!SyncManager.instance) {
            SyncManager.instance = new SyncManager();
        }
        return SyncManager.instance;
    }

    startAutoSync(intervalMs: number = 30000) {
        if (this.intervalId) return;
        this.sync(); // Initial sync
        this.intervalId = setInterval(() => this.sync(), intervalMs);
    }

    stopAutoSync() {
        if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }
    }

    subscribe(listener: (status: 'Online' | 'Offline' | 'Syncing') => void) {
        this.statusListeners.add(listener);
        // Initial callback
        listener(this.isSyncing ? 'Syncing' : this.networkStatus);
        return () => this.statusListeners.delete(listener);
    }

    private notifyListeners() {
        const status = this.isSyncing ? 'Syncing' : this.networkStatus;
        this.statusListeners.forEach(l => l(status));
    }

    async sync() {
        if (this.isSyncing || this.networkStatus === 'Offline') return;

        this.isSyncing = true;
        this.notifyListeners();

        try {
            console.log('🔄 Starting Cloud Sync...');

            // We will iterate through all stores in api.ts
            // Note: This relies on api.ts structure containing providers
            const stores = [
                'projects', 'issues', 'evm', 'risks', 'budgetItems',
                'reports', 'documents', 'settings', 'tasks',
                'checklistItems', 'proposals', 'changeOrders',
                'complianceRequirements', 'subcontractors',
                'wbsTasks', 'projectLogs', 'auditLogs'
            ];

            for (const storeKey of stores) {
                // @ts-ignore - Dynamic access to API providers
                const provider = api[storeKey];
                if (provider && typeof provider.getPending === 'function') {
                    const pendingItems = await provider.getPending();

                    if (pendingItems.length > 0) {
                        console.log(`📤 Syncing ${pendingItems.length} items for ${storeKey}...`);

                        for (const item of pendingItems) {
                            try {
                                // Push to Cloudflare D1 (Simulation for now as backend endpoint might not exist for all)
                                // In real implementation: await fetch(`${BACKEND_URL}/${storeKey}`, { method: 'POST', body: JSON.stringify(item) })

                                // Simulate network delay
                                await new Promise(r => setTimeout(r, 50));

                                // Mark as synced locally
                                // @ts-ignore
                                if (item.id) await provider.markSynced(item.id);

                            } catch (err) {
                                console.error(`Failed to push item ${item.id} in ${storeKey}`, err);
                            }
                        }
                    }
                }
            }

            console.log('✅ Cloud Sync Complete');
        } catch (err) {
            console.error('❌ Cloud Sync Critical Failure', err);
        } finally {
            this.isSyncing = false;
            this.notifyListeners();
        }
    }
}
